import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { FC, useEffect, useRef, useState } from "react";

import IconButton from '@mui/material/IconButton';
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarExport, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Tooltip, Typography } from "@mui/material";

import EditIcon from '@mui/icons-material/Edit';
import MonitorIcon from '@mui/icons-material/Monitor';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { IccUser } from "../../../../../models/AdminPageModels";
import { PaginationModel } from "../../../../../models/ReportGridModels";


interface AdminIccUsersResponse {
    data: IccUsersData;
    error: IError;
}
interface IccUsersData {
    users: IccUser[];
}

interface DeleteIccUserRequest {
    id: number;
}

interface DeleteIccUserResponse {
    data: DeleteIccUserData;
    error: IError;
}
interface DeleteIccUserData {
    status: string;
}

interface ViewIccUsersProsp {
    editUser: (user: IccUser) => void;
}

const headerHeight: number = 100;
function GetGridHeight() {
    return window.innerHeight - headerHeight;
}
const menuWidth: number = 92;
function GetGridWidth() {
    return window.innerWidth - menuWidth;
}

function ColyToClipBoard(text: string) {
    navigator.clipboard.writeText(text);
};

const ViewIccUsers: FC<ViewIccUsersProsp> = ({ editUser }) => {
    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 10 },
        { field: 'username', headerName: 'Username', width: 200 },
        { // password
            field: 'password', headerName: 'Password', width: 165,
            renderCell: (param: any) => (
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent="space-between"
                    sx={{ width: "200px" }}>
                    <p>{param.value}</p>

                    <IconButton onClick={() => { ColyToClipBoard(`u: ${param.row.username}\np: ${param.value}`) }}>
                        <ContentCopyIcon />
                    </IconButton>
                </Stack >
            )
        },
        { field: 'companyName', headerName: 'Company', width: 130 },
        { field: 'boothID', headerName: 'BoothID', width: 100 },
        { // createDate
            field: 'createDate', headerName: 'Creation Date', width: 160, renderCell: (param: any) => (
                <p>{dayjs(param.row.createDate).format("YYYY-MM-DD HH:mm:ss")}</p>
            )
        },
        { // lastLoginDesktop
            field: 'lastLoginDesktop', headerName: 'ICC last login', width: 160, renderCell: (param: any) => (
                <p>{dayjs(param.row.lastLoginDesktop).format("YYYY-MM-DD HH:mm:ss")}</p>
            )
        },
        { // lastLoginMobile
            field: 'lastLoginMobile', headerName: 'IOS last login', width: 160, renderCell: (param: any) => (
                <p>{dayjs(param.row.lastLoginMobile).format("YYYY-MM-DD HH:mm:ss")}</p>
            )
        },
        { field: 'loginCount', headerName: 'Login Count', width: 87 },
        { field: 'attempts', headerName: 'Attempts', width: 70 },
        { // isLastLoginIOS
            field: 'isLastLoginIOS', headerName: 'Last', width: 60, renderCell: (param: any) => (
                <>
                    {param.row.isLastLoginIOS === 1 ?
                        <PhoneIphoneIcon />
                        :
                        <MonitorIcon />
                    }
                </>
            )
        },
        { // appVersionDesktop
            field: 'desktopVersionState', headerName: 'ICC version', width: 90, renderCell: (param: any) => (
                <>
                    {param.row.desktopVersionState === 0 ?
                        <p style={{ maxWidth: "90px", padding: "10px", backgroundColor: "#83f28f"/*"green"*/ }}>{param.row.appVersionDesktop}</p>
                        : null}
                    {param.row.desktopVersionState === 1 ?
                        <p style={{ maxWidth: "90px", padding: "10px", backgroundColor: "yellow" }}>{param.row.appVersionDesktop}</p>
                        : null}
                    {param.row.desktopVersionState === 3 ?
                        <p style={{ maxWidth: "90px", padding: "10px", backgroundColor: "#ff474c" /*"red"*/ }}>{param.row.appVersionDesktop}</p>
                        : null}
                    {param.row.desktopVersionState === -1 ?
                        <p style={{ maxWidth: "90px", padding: "10px", backgroundColor: "#800080"/*"purple"*/ }}>{param.row.appVersionDesktop}</p>
                        : null}
                </>
            )
        },
        { // appVersionMobile
            field: 'mobileVersionState', headerName: 'IOS version', width: 110, renderCell: (param: any) => (
                <>
                    {param.row.mobileVersionState === 0 ?
                        <p style={{ maxWidth: "110px", padding: "10px", backgroundColor: "#83f28f"/*"green"*/ }}>{param.row.appVersionMobile}</p>
                        : null}
                    {param.row.mobileVersionState === 1 ?
                        <p style={{ maxWidth: "110px", padding: "10px", backgroundColor: "yellow" }}>{param.row.appVersionMobile}</p>
                        : null}
                    {param.row.mobileVersionState === 3 ?
                        <p style={{ maxWidth: "110px", padding: "10px", backgroundColor: "#ff474c" /*"red"*/ }}>{param.row.appVersionMobile}</p>
                        : null}
                    {param.row.mobileVersionState === -1 ?
                        <p style={{ maxWidth: "110px", padding: "10px", backgroundColor: "#800080"/*"purple"*/, color: "white" }}>{param.row.appVersionMobile}</p>
                        : null}
                </>
            )
        },
        { // versionUpdatedDesktop
            field: 'versionUpdatedDesktop', headerName: 'ICC update date', width: 160, renderCell: (param: any) => (
                <p>{dayjs(param.row.versionUpdatedDesktop).format("YYYY-MM-DD HH:mm:ss")}</p>
            )
        },
        { // versionUpdatedMobile
            field: 'versionUpdatedMobile', headerName: 'IOS update date', width: 160, renderCell: (param: any) => (
                <p>{dayjs(param.row.versionUpdatedMobile).format("YYYY-MM-DD HH:mm:ss")}</p>
            )
        },
        { // iPhoneModelIOS
            field: 'iPhoneModelIOS', headerName: 'Phone models', width: 160, renderCell: (params: any) => (
                <>
                    {params.value.length != 0 ?
                        <>
                            <Tooltip
                                title={
                                    <Typography
                                        fontSize={"16px"}
                                        sx={{ whiteSpace: "pre-line" }}>
                                        {params.value}
                                    </Typography>
                                }>
                                <p>{params.value}</p>
                            </Tooltip>
                        </>
                        : null
                    }
                </>
            )
        },
        { // edit
            field: 'edit', headerName: 'Edit', width: 50,
            renderCell: (param: any) => (
                <IconButton onClick={() => { editUser(param.row) }}>
                    <EditIcon />
                </IconButton>
            )
        },
        { // delete
            field: 'delete', headerName: 'Delete', width: 50,
            renderCell: (param: any) => (
                <IconButton onClick={() => { AskToDeleteUser(param.row) }}>
                    <DeleteForeverIcon htmlColor="red" />
                </IconButton>
            )
        }
    ];

    const { enqueueSnackbar } = useSnackbar();

    const [showEnabledOnly, setShowEnabledOnly] = useState<boolean>(true);
    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 200,
        page: 0,
    });
    const [users, setUsers] = useState<IccUser[]>([]);

    const usersCopyRef = useRef<IccUser[]>([]);
    const userToDelete = useRef<IccUser | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);

    useEffect(() => {
        GetIccUsers();
    }, []);

    const GetIccUsers = async () => {
        try {
            const response: AdminIccUsersResponse = await api.get("api/Admin/GetICCUsers", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                usersCopyRef.current = response.data.users;

                setUsers(response.data.users.filter((user: IccUser) => user.enabled === true));
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function AskToDeleteUser(user: IccUser) {
        userToDelete.current = user;
        setDeleteConfirmation(true);
    }

    function closeDeleteConfirmation() {
        setDeleteConfirmation(false);
    }

    const DeleteUser = async () => {
        if (userToDelete.current == null)
            return;

        var body: DeleteIccUserRequest = {
            id: userToDelete.current.id
        }

        try {
            const response: DeleteIccUserResponse = await api.post("api/Admin/DeleteIccUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Deleted!", { variant: "success" });

                // get new users from db
                GetIccUsers();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function FilterUsers(event: React.ChangeEvent<HTMLInputElement>) {
        setShowEnabledOnly(event.target.checked);

        if (event.target.checked) {
            setUsers(usersCopyRef.current.filter((user: IccUser) => user.enabled === true));
        }
        else {
            setUsers(usersCopyRef.current);
        }
    }

    const CustomToolbar = () => (
        <GridToolbarContainer>
            <GridToolbarExport />
            <GridToolbarQuickFilter
                quickFilterParser={(quickFilterValue: string) => [quickFilterValue]}
                sx={{ marginLeft: "10px" }} />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={showEnabledOnly}
                        onChange={FilterUsers}
                        color="primary"
                    />
                }
                label="Show Enabled Only"
                sx={{ marginLeft: "10px" }}
            />
        </GridToolbarContainer>
    );

    return (
        <>
            <DataGrid
                sx={{ height: GetGridHeight(), width: GetGridWidth(), marginTop: "-15px", marginLeft: "-15px", marginBottom: "-20px", marginRight: "-15px" }}
                rows={users}
                columns={columns}
                pagination
                pageSizeOptions={[10, 15, 20, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                slots={{ toolbar: CustomToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 100 },
                    },
                }} />

            <Dialog
                open={deleteConfirmation}
                onClose={closeDeleteConfirmation}>
                <DialogTitle>
                    {"ICC user delete confirmation"}
                </DialogTitle>
                <DialogContent>
                    Are you sure you want to remove this user permanently?
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteConfirmation}>Cancel</Button>
                    <Button color="error" onClick={() => {
                        closeDeleteConfirmation();
                        DeleteUser();
                    }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog >
        </>
    )
}

export default ViewIccUsers;